import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { Button } from '@/components/button'
import { Field, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { restaurantFeedbackQuery } from '@/features/feedback/api'
import { FeedbackEntryRow } from '@/features/feedback/entry-row'
import { useManagedRestaurant } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'
import { canManageRestaurant } from '@/lib/roles'

const ROUTE_ID = '/$restaurantSlug/manage/feedback'
const PATH = '/$restaurantSlug/manage/feedback'

/**
 * What customers wrote about the restaurant (I3): from an order's tracking
 * page or a landing page, newest first. Manager and above, like the audit log.
 */
export function RestaurantFeedbackPage() {
  const t = useT()
  const { restaurantId, context } = useManagedRestaurant()
  return (
    <Page width="lg" title={t('feedback.title')} className="flex flex-col gap-4">
      <p className="text-muted text-sm">{t('feedback.intro')}</p>
      {canManageRestaurant(context.role) ? (
        <FeedbackList restaurantId={restaurantId} branches={context.branches} />
      ) : (
        <p role="alert" className="text-muted text-sm">
          {t('feedback.managerOnly')}
        </p>
      )}
    </Page>
  )
}

function FeedbackList({ restaurantId, branches }: { restaurantId: string; branches: { id: string; name: string }[] }) {
  const t = useT()
  const navigate = useNavigate()
  const { page, branch } = useSearch({ from: ROUTE_ID })
  const list = useQuery(restaurantFeedbackQuery(restaurantId, branch, page))
  const { restaurantSlug } = useManagedRestaurant()
  const params = { restaurantSlug }
  const link = (page: number) => ({ to: PATH, params, search: { page, ...(branch ? { branch } : {}) } }) as const
  useClampedPage(page, list.data, link)

  return (
    <>
      <Card className="grid gap-3 sm:grid-cols-2">
        <Field label={t('feedback.filter.branch')}>
          <Select
            value={branch ?? ''}
            onChange={(event) => {
              const value = event.target.value
              void navigate({ to: PATH, params, search: value ? { page: 1, branch: value } : { page: 1 } })
            }}
          >
            <option value="">{t('feedback.filter.allBranches')}</option>
            {branches.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </Field>
      </Card>

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t('feedback.empty')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((entry) => (
              <li key={entry.id}>
                <FeedbackEntryRow entry={entry} />
              </li>
            ))}
          </ul>
        ))}
      <Pager label={t('feedback.pager')} page={page} total={list.data?.total ?? 0} link={link} />
    </>
  )
}
